import { createTeamSchema } from '@nexo/types';
import { Button, Field, Input, Modal } from '@nexo/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, type SubmitEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { api } from '../../lib/api';
import { errorMessage, validate } from '../../lib/forms';
import { keys } from '../../lib/queries';

export function CreateTeamForm({
  onCreated,
  autoFocus,
}: {
  onCreated?: () => void;
  autoFocus?: boolean;
}) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [error, setError] = useState<string | undefined>();
  const { t } = useTranslation();

  const mutation = useMutation({
    mutationFn: api.teams.create,
    onSuccess: async (team) => {
      await queryClient.invalidateQueries({ queryKey: keys.teams });
      onCreated?.();
      void navigate(`/app/t/${team.id}`);
    },
    onError: (err) => setError(errorMessage(err)),
  });

  function onSubmit(event: SubmitEvent) {
    event.preventDefault();
    const { data, errors } = validate(createTeamSchema, { name });
    if (errors) return setError(errors.name);
    mutation.mutate(data);
  }

  return (
    <form onSubmit={onSubmit} noValidate className="flex flex-col gap-4">
      <Field label={t('Nombre del equipo')} error={error}>
        {(props) => (
          <Input
            {...props}
            autoFocus={autoFocus}
            placeholder={t('p. ej. Estudio Alba')}
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              setError(undefined);
            }}
          />
        )}
      </Field>
      <Button type="submit" loading={mutation.isPending}>
        {t('Crear equipo')}
      </Button>
    </form>
  );
}

export function CreateTeamDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('Crear un equipo')}
      description={t('Tendrás un canal #general listo para empezar.')}
      size="sm"
    >
      <CreateTeamForm onCreated={onClose} autoFocus />
    </Modal>
  );
}
